import {
  HubConnection,
  HubConnectionBuilder,
  LogLevel,
} from '@microsoft/signalr'
import { useEffect, useState } from 'react'

import { env } from '@/env'
import { api } from '@/services/axios'

interface UseTicketHubReturn {
  blockedNumbers: number[]
  paidNumbers: number[]
  pendingNumbers: number[]
  connection: HubConnection | null
  numbersLoaded: boolean
  error: string | null
  selectNumber: (number: number) => Promise<void>
  unselectNumber: (number: number) => Promise<void>
  confirmPayment: (numbers: number[]) => Promise<void>
  markPendingPayment: (numbers: number[]) => Promise<void>
  blockedNumbersInfo: Record<number, string>
}

export const useTicketHub = (campaignId: number): UseTicketHubReturn => {
  const [connection, setConnection] = useState<HubConnection | null>(null)
  const [blockedNumbers, setBlockedNumbers] = useState<number[]>([])
  const [paidNumbers, setPaidNumbers] = useState<number[]>([])
  const [pendingNumbers, setPendingNumbers] = useState<number[]>([])
  const [numbersLoaded, setNumbersLoaded] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [userSelectedNumbers, setUserSelectedNumbers] = useState<number[]>([])
  const [blockedNumbersInfo, setBlockedNumbersInfo] = useState<
    Record<number, string>
  >({})
  const [userId] = useState<string>(() => {
    const stored = localStorage.getItem('userId')
    const newId = stored || `user-${crypto.randomUUID()}`
    if (!stored) localStorage.setItem('userId', newId)
    return newId
  })

  const getPaymentByCampaignId = async (rifaId: number) => {
    const response = await api.get(
      `Pagamento/ListarPagamentosPorCampanha?campanhaId=${rifaId}`,
    )
    return response.data
  }

  const getPaymentByPaymentId = async (paymentId: number) => {
    try {
      const response = await api.get(
        `Pagamento/ObterPagamentoPorId/${paymentId}`,
      )
      return response.data
    } catch (error) {
      console.error('Error fetching payment:', error)
      return null
    }
  }

  const fetchInitialNumbers = async () => {
    try {
      const payments = await getPaymentByCampaignId(campaignId)
      const paid: number[] = []
      const pending: number[] = []

      await Promise.all(
        payments.map(async (payment: any) => {
          const details = await getPaymentByPaymentId(payment.pagamentoId)
          const numero = Number(payment.titulo)
          if (details?.status === 'approved' || details?.status === 'paid') {
            paid.push(numero)
          } else if (details?.status === 'pending') {
            pending.push(numero)
          }
        }),
      )

      return { paid, pending }
    } catch (error) {
      console.error('Error fetching initial numbers:', error)
      return { paid: [], pending: [] }
    }
  }

  const startConnection = async () => {
    try {
      const newConnection = new HubConnectionBuilder()
        .withUrl(`${env.VITE_API_URL}ticketHub`, {
          skipNegotiation: true,
          transport: 1,
        })
        .configureLogging(LogLevel.Information)
        .withAutomaticReconnect({
          nextRetryDelayInMilliseconds: (retryContext) => {
            if (retryContext.elapsedMilliseconds < 60000) {
              return 2000
            }
            return null
          },
        })
        .build()

      newConnection.on(
        'ReceiveInitialNumbers',
        async (data: {
          blockedNumbers: number[]
          paidNumbers: number[]
          pendingNumbers: number[]
        }) => {
          const fetched = await fetchInitialNumbers()

          const allBlockedNumbers = [
            ...new Set([
              ...(data.blockedNumbers ?? []),
              ...(data.paidNumbers ?? []),
            ]),
          ]

          setBlockedNumbers(allBlockedNumbers)
          setPaidNumbers([
            ...new Set([...(data.paidNumbers ?? []), ...fetched.paid]),
          ])
          setPendingNumbers([
            ...new Set([...(data.pendingNumbers ?? []), ...fetched.pending]),
          ])
          setNumbersLoaded(true)
        },
      )

      newConnection.on('NumberSelected', (number: number) => {
        setBlockedNumbers((prev) => [...new Set([...(prev ?? []), number])])
      })

      newConnection.on('NumberUnselected', (number: number) => {
        setBlockedNumbers((prev) => (prev ?? []).filter((n) => n !== number))
        setUserSelectedNumbers((prev) => prev.filter((n) => n !== number))
      })

      newConnection.on('NumbersPermanentlyBlocked', (numbers: number[]) => {
        setPaidNumbers((prev) => [...new Set([...(prev ?? []), ...numbers])])
        setBlockedNumbers((prev) => [...new Set([...(prev ?? []), ...numbers])])
        setUserSelectedNumbers((prev) =>
          prev.filter((n) => !numbers.includes(n)),
        )
      })

      newConnection.on('NumbersPendingBlocked', (numbers: number[]) => {
        setPendingNumbers((prev) => [...new Set([...(prev ?? []), ...numbers])])
        setBlockedNumbers((prev) => [...new Set([...(prev ?? []), ...numbers])])
        setUserSelectedNumbers((prev) =>
          prev.filter((n) => !numbers.includes(n)),
        )
      })

      newConnection.on(
        'receiveupdatednumbers',
        (data: {
          paidNumbers: number[]
          pendingNumbers: number[]
          blockedNumbers: number[]
          blockedNumbersInfo?: Record<number, string | Date>
        }) => {
          setPaidNumbers((prev) => [
            ...new Set([...(prev ?? []), ...(data.paidNumbers ?? [])]),
          ])

          setPendingNumbers((prev) => [
            ...new Set([...(prev ?? []), ...(data.pendingNumbers ?? [])]),
          ])

          setBlockedNumbers((prev) => [
            ...new Set([
              ...(prev ?? []),
              ...(data.blockedNumbers ?? []),
              ...(data.paidNumbers ?? []),
            ]),
          ])

          if (data.blockedNumbersInfo) {
            const formattedInfo: Record<number, string> = {}
            Object.entries(data.blockedNumbersInfo).forEach(([key, value]) => {
              const date = new Date(value)
              formattedInfo[parseInt(key)] = date.toLocaleString('pt-BR')
            })
            setBlockedNumbersInfo(formattedInfo)
          }

          setUserSelectedNumbers((prev) =>
            prev.filter(
              (n) =>
                ![
                  ...data.paidNumbers,
                  ...data.pendingNumbers,
                  ...data.blockedNumbers,
                ].includes(n),
            ),
          )

          if (
            data.paidNumbers.length === 0 &&
            data.pendingNumbers.length === 0 &&
            data.blockedNumbers.length === 0
          ) {
            setBlockedNumbers([])
            setPaidNumbers([])
            setPendingNumbers([])
          }
        },
      )

      newConnection.onclose((error) => {
        const msg = error
          ? `Connection closed: ${error.message}`
          : 'Connection closed'
        console.error(msg)
        setError(msg)
      })

      newConnection.onreconnecting((error) => {
        const msg = error ? `Reconnecting: ${error.message}` : 'Reconnecting...'
        console.warn(msg)
        setError(msg)
      })

      newConnection.onreconnected(async (connectionId) => {
        console.log(`Reconnected with ID: ${connectionId}`)
        setError(null)

        try {
          await newConnection.invoke(
            'SubscribeToCampaign',
            Number(campaignId),
            userId,
          )
          console.log('Resubscribed after reconnect')
        } catch (err) {
          console.error('Failed to resubscribe after reconnect', err)
        }
      })

      await newConnection.start()
      console.log('SignalR Connected')

      try {
        await newConnection.invoke(
          'SubscribeToCampaign',
          Number(campaignId),
          userId,
        )
        console.log('Successfully subscribed to campaign')
      } catch (invokeError) {
        console.error('Failed to invoke SubscribeToCampaign:', invokeError)
        throw invokeError
      }

      return newConnection
    } catch (err) {
      console.error('Connection failed:', err)
      setError('Failed to connect. Trying again...')
      throw err
    }
  }

  useEffect(() => {
    localStorage.setItem('userId', userId)

    let isActive = true
    let connectionInstance: HubConnection | null = null

    const connect = async () => {
      try {
        console.log('Initializing connection...')
        const conn = await startConnection()
        if (isActive) {
          connectionInstance = conn
          setConnection(conn)
        }
      } catch (error) {
        if (isActive) {
          console.log('Retrying connection in 5 seconds...')
          setTimeout(connect, 5000)
        }
      }
    }

    connect()

    return () => {
      isActive = false
      if (connectionInstance) {
        console.log('Disconnecting SignalR...')
        connectionInstance.stop().catch(console.error)
      }
    }
  }, [campaignId, userId])

  useEffect(() => {
    if (connection) {
      connection.on('NumberSelected', (number: number) => {
        setBlockedNumbers((prev) => [...new Set([...prev, number])])
      })

      connection.on('NumberUnselected', (number: number) => {
        setBlockedNumbers((prev) => prev.filter((n) => n !== number))
      })

      connection.on('NumbersPermanentlyBlocked', (numbers: number[]) => {
        setPaidNumbers((prev) => [...new Set([...prev, ...numbers])])
        setBlockedNumbers((prev) => [...new Set([...prev, ...numbers])])
      })

      connection.on('NumbersPendingBlocked', (numbers: number[]) => {
        setPendingNumbers((prev) => [...new Set([...prev, ...numbers])])
        setBlockedNumbers((prev) => [...new Set([...prev, ...numbers])])
      })
    }

    return () => {
      if (connection) {
        connection.off('NumberSelected')
        connection.off('NumberUnselected')
        connection.off('NumbersPermanentlyBlocked')
        connection.off('NumbersPendingBlocked')
      }
    }
  }, [connection])

  function selectNumber(number: number): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      const handler = (confirmedNumber: number) => {
        if (confirmedNumber === number) {
          connection?.off('NumberSelectionConfirmed', handler)
          resolve()
        }
      }

      connection?.on('NumberSelectionConfirmed', handler)

      connection
        ?.invoke('SelectNumber', Number(campaignId), number, userId)
        .catch((error) => {
          connection.off('NumberSelectionConfirmed', handler)
          reject(error)
        })

      setTimeout(() => {
        connection?.off('NumberSelectionConfirmed', handler)
        reject(new Error(`Timeout ao selecionar número ${number}`))
      }, 5000)
    })
  }

  const unselectNumber = async (number: number) => {
    if (!connection) {
      console.error('No server connection')
      throw new Error('No server connection')
    }

    try {
      setUserSelectedNumbers((prev) => prev.filter((n) => n !== number))

      await connection.invoke(
        'UnselectNumber',
        Number(campaignId),
        number,
        userId,
      )
    } catch (error) {
      console.error('Error unselecting number:', error)
      setUserSelectedNumbers((prev) => [...prev, number])
      throw error
    }
  }
  const markPendingPayment = async (numbers: number[]) => {
    if (!connection) throw new Error('No server connection')
    try {
      await connection.invoke('MarkPendingPayment', Number(campaignId), numbers)
      setPendingNumbers((prev) => [...new Set([...(prev ?? []), ...numbers])])
      setBlockedNumbers((prev) => [...new Set([...(prev ?? []), ...numbers])])
      setUserSelectedNumbers((prev) => prev.filter((n) => !numbers.includes(n)))
    } catch (error) {
      console.error('Error mark pending:', error)
      throw error
    }
  }

  const confirmPayment = async (numbers: number[]) => {
    if (!connection) throw new Error('No server connection')
    try {
      await connection.invoke('ConfirmPayment', Number(campaignId), numbers)

      setPendingNumbers((prev) => prev.filter((n) => !numbers.includes(n)))
      setPaidNumbers((prev) => [...new Set([...(prev ?? []), ...numbers])])
      setBlockedNumbers((prev) => [...new Set([...(prev ?? []), ...numbers])])
      setUserSelectedNumbers((prev) => prev.filter((n) => !numbers.includes(n)))
    } catch (error) {
      console.error('Error confirming payment:', error)
      throw error
    }
  }

  return {
    blockedNumbers: [...new Set([...blockedNumbers, ...userSelectedNumbers])],
    paidNumbers,
    pendingNumbers,
    connection,
    numbersLoaded,
    error,
    selectNumber,
    unselectNumber,
    confirmPayment,
    markPendingPayment,
    blockedNumbersInfo,
  }
}
